import React, { useState, useEffect } from 'react';
import type { Job, CreateJobRequest } from '../api/jobs';
import type { Server } from '../api/servers';
import { getNextNExecutions } from '../utils/timeHelpers';
import './JobModal.css';

interface JobModalProps {
  job?: Job | null;
  servers: Server[];
  onSave: (job: CreateJobRequest | Partial<Job>) => void;
  onClose: () => void;
  isLoading: boolean;
}

const INSTANCE_TYPES = [
  'm6a.large',
  'm6a.xlarge',
  'm6a.2xlarge',
  'm6a.4xlarge',
  'm7i.large',
  'm7i.xlarge',
  'm7i.2xlarge',
  'm7i.4xlarge',
  'm7i.8xlarge',
  'm7i.12xlarge',
  'c7i.large',
  'c7i.xlarge',
  'c7i.2xlarge',
  'c7i.4xlarge',
  'c7i.8xlarge',
  'c7i.12xlarge',
  'r7i.large',
  'r7i.xlarge',
  'r7i.2xlarge',
  'r7i.4xlarge',
  'r7i.8xlarge',
  't3a.medium',
  't3a.large',
  't3a.xlarge',
  't3a.2xlarge',
];

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export const JobModal: React.FC<JobModalProps> = ({ job, servers, onSave, onClose, isLoading }) => {
  const [formData, setFormData] = useState({
    instanceId: '',
    instanceName: '',
    region: 'eu-west-1',
    action: 'start' as 'start' | 'stop',
    instanceType: '',
    daysOfWeek: [] as number[],
    hour: 8,
    minute: 0,
    description: '',
    enabled: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get unique regions from servers
  const availableRegions = Array.from(new Set(servers.map((s) => s.region))).sort();

  // Filter servers by selected region
  const serversInRegion = servers.filter((s) => s.region === formData.region);

  // Handle server selection
  const handleServerChange = (instanceId: string) => {
    const selectedServer = servers.find((s) => s.instanceId === instanceId);
    if (selectedServer) {
      setFormData((prev) => ({
        ...prev,
        instanceId: selectedServer.instanceId,
        instanceName: selectedServer.instanceName,
        region: selectedServer.region,
      }));
    }
  };

  useEffect(() => {
    if (job) {
      setFormData({
        instanceId: job.instanceId,
        instanceName: job.instanceName || '',
        region: job.region,
        action: job.action,
        instanceType: job.instanceType || '',
        daysOfWeek: job.schedule.daysOfWeek,
        hour: job.schedule.hour,
        minute: job.schedule.minute,
        description: job.description || '',
        enabled: job.enabled,
      });
    }
  }, [job]);

  // Set default region when servers load (for new jobs)
  useEffect(() => {
    if (!job && availableRegions.length > 0 && !formData.region) {
      setFormData((prev) => ({ ...prev, region: availableRegions[0] }));
    }
  }, [availableRegions, job, formData.region]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.instanceId.trim()) {
      newErrors.instanceId = 'Instance ID is required';
    }

    if (formData.daysOfWeek.length === 0) {
      newErrors.daysOfWeek = 'At least one day must be selected';
    }

    if (formData.action === 'start' && !formData.instanceType) {
      newErrors.instanceType = 'Instance type is required for start action';
    }

    if (formData.hour < 0 || formData.hour > 23) {
      newErrors.hour = 'Hour must be between 0 and 23';
    }

    if (formData.minute < 0 || formData.minute > 59) {
      newErrors.minute = 'Minute must be between 0 and 59';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const jobData = {
      instanceId: formData.instanceId,
      instanceName: formData.instanceName || undefined,
      region: formData.region,
      action: formData.action,
      schedule: {
        daysOfWeek: formData.daysOfWeek,
        hour: formData.hour,
        minute: formData.minute,
      },
      description: formData.description || undefined,
      instanceType: formData.action === 'start' ? formData.instanceType : undefined,
      enabled: formData.enabled,
    };

    onSave(jobData);
  };

  const handleDayToggle = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort((a, b) => a - b),
    }));
  };

  const handleQuickDays = (type: 'weekdays' | 'weekends' | 'all') => {
    let days: number[] = [];
    switch (type) {
      case 'weekdays':
        days = [1, 2, 3, 4, 5];
        break;
      case 'weekends':
        days = [0, 6];
        break;
      case 'all':
        days = [0, 1, 2, 3, 4, 5, 6];
        break;
    }
    setFormData((prev) => ({ ...prev, daysOfWeek: days }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{job ? 'Edit Job' : 'Create New Job'}</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="job-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="region">Region *</label>
              <select
                id="region"
                value={formData.region}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    region: e.target.value,
                    instanceId: '',
                    instanceName: '',
                  }))
                }
              >
                {availableRegions.length > 0 ? (
                  availableRegions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))
                ) : (
                  <option value="">No regions available</option>
                )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="server">Server *</label>
              <select
                id="server"
                value={formData.instanceId}
                onChange={(e) => handleServerChange(e.target.value)}
                className={errors.instanceId ? 'error' : ''}
              >
                <option value="">Select a server...</option>
                {serversInRegion.map((server) => (
                  <option key={server.instanceId} value={server.instanceId}>
                    {server.instanceName} ({server.instanceId}) - {server.instanceType} [
                    {server.state}]
                  </option>
                ))}
              </select>
              {errors.instanceId && <span className="error-text">{errors.instanceId}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="action">Action *</label>
              <select
                id="action"
                value={formData.action}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    action: e.target.value as 'start' | 'stop',
                    instanceType: e.target.value === 'stop' ? '' : prev.instanceType,
                  }))
                }
              >
                <option value="start">Start Instance</option>
                <option value="stop">Stop Instance</option>
              </select>
            </div>

            {formData.action === 'start' && (
              <div className="form-group">
                <label htmlFor="instanceType">
                  Instance Type *
                  {formData.action === 'start' && (
                    <span className="field-hint"> (what type to start as)</span>
                  )}
                </label>
                <select
                  id="instanceType"
                  value={formData.instanceType}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, instanceType: e.target.value }))
                  }
                  className={errors.instanceType ? 'error' : ''}
                >
                  <option value="">Select instance type...</option>
                  {INSTANCE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.instanceType && <span className="error-text">{errors.instanceType}</span>}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Schedule Days *</label>
            <div className="quick-days">
              <button type="button" onClick={() => handleQuickDays('weekdays')}>
                Weekdays
              </button>
              <button type="button" onClick={() => handleQuickDays('weekends')}>
                Weekends
              </button>
              <button type="button" onClick={() => handleQuickDays('all')}>
                All Days
              </button>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, daysOfWeek: [] }))}
              >
                Clear
              </button>
            </div>
            <div className="days-grid">
              {DAYS.map((day) => (
                <label key={day.value} className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.daysOfWeek.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                  />
                  <span className="day-label">{day.label}</span>
                </label>
              ))}
            </div>
            {errors.daysOfWeek && <span className="error-text">{errors.daysOfWeek}</span>}
          </div>

          <div className="time-section">
            <div className="time-inputs">
              <div className="form-group">
                <label htmlFor="hour">
                  Hour (0-23) * <span className="label-hint">(UTC)</span>
                </label>
                <input
                  id="hour"
                  type="number"
                  min="0"
                  max="23"
                  value={formData.hour}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, hour: parseInt(e.target.value) || 0 }))
                  }
                  className={errors.hour ? 'error' : ''}
                />
                {errors.hour && <span className="error-text">{errors.hour}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="minute">
                  Minute (0-59) * <span className="label-hint">(UTC)</span>
                </label>
                <input
                  id="minute"
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minute}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, minute: parseInt(e.target.value) || 0 }))
                  }
                  className={errors.minute ? 'error' : ''}
                />
                {errors.minute && <span className="error-text">{errors.minute}</span>}
              </div>
            </div>

            {formData.daysOfWeek.length > 0 && (
              <div className="next-runs-preview">
                <h4>Next 5 Scheduled Runs</h4>
                <div className="next-runs-list">
                  {getNextNExecutions(formData.daysOfWeek, formData.hour, formData.minute, 5).map(
                    (date, index) => {
                      // UTC date info
                      const utcDayName = date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        timeZone: 'UTC',
                      });
                      const utcDateStr = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        timeZone: 'UTC',
                      });
                      const utcTime = `${String(formData.hour).padStart(2, '0')}:${String(formData.minute).padStart(2, '0')}`;

                      // Local date info
                      const localDayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                      const localDateStr = date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      });
                      const localTime = date.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      });
                      const timezone =
                        new Intl.DateTimeFormat('en', { timeZoneName: 'short' })
                          .formatToParts(date)
                          .find((part) => part.type === 'timeZoneName')?.value || '';

                      // Check if dates are different
                      const datesAreDifferent = utcDateStr !== localDateStr;

                      return (
                        <div key={index} className="next-run-item">
                          <span className="run-number">Run #{index + 1}</span>
                          <div className="run-time">
                            <div className="run-datetime">
                              <div className="datetime-row">
                                <span className="datetime-label">UTC:</span>
                                <span className="datetime-value">
                                  {utcDayName}, {utcDateStr} at {utcTime}
                                </span>
                              </div>
                              <div className="datetime-row">
                                <span className="datetime-label">Local:</span>
                                <span
                                  className={`datetime-value ${datesAreDifferent ? 'date-different' : ''}`}
                                >
                                  {localDayName}, {localDateStr} at {localTime} {timezone}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
                {formData.daysOfWeek.length === 7 && (
                  <div className="warning-message">
                    <span>Note:</span>
                    <span>This job will run every day at the specified time</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Optional description for this job"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
              <span>Enable this job immediately</span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : job ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
